import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import VectorIcon from 'assets/VectorIcon.svg';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import { Select } from 'common-ui/select';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import { getWorkflowStatuses } from 'features/newsales/newsalesActions';
import { selectWorkflowStatuses } from 'features/newsales/newsalesSlice';
import * as Yup from 'yup';

import { ErrorText } from '../OfferModal/OfferModal.styles';
import { ModalFormContent, SwitchWrapper } from './UserPrivilegeModal.styles';

const UserPrivilegeModal = ({
  mode = 'create',
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  initialData,
}) => {
  const isEditMode = mode === 'edit';
  const dispatch = useDispatch();
  const workflowStatuses = useSelector(selectWorkflowStatuses);

  const [errors, setErrors] = useState({
    name: '',
    viewableStatuses: '',
    changeableStatuses: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    viewableStatuses: [],
    changeableStatuses: [],
    enabled: true,
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(getWorkflowStatuses({ page: 1, limit: 100 }));
    }
  }, [isOpen, dispatch]);

  const statusOptions = workflowStatuses
    ? workflowStatuses.map((status) => ({
        label: status.name,
        value: status.id,
      }))
    : [];

  useEffect(() => {
    if (
      isOpen &&
      isEditMode &&
      initialData &&
      workflowStatuses &&
      workflowStatuses.length > 0
    ) {
      const options = workflowStatuses.map((status) => ({
        label: status.name,
        value: status.id,
      }));

      const selectedViewableStatuses = initialData.viewableStatuses
        ? initialData.viewableStatuses
            .map((status) => options.find((option) => option.value === status.id))
            .filter(Boolean)
        : [];

      const selectedChangeableStatuses = initialData.changeableStatuses
        ? initialData.changeableStatuses
            .map((status) => options.find((option) => option.value === status.id))
            .filter(Boolean)
        : [];

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        viewableStatuses: selectedViewableStatuses,
        changeableStatuses: selectedChangeableStatuses,
        enabled: initialData.enabled ?? true,
      });

      setErrors({
        name: '',
        viewableStatuses: '',
        changeableStatuses: '',
      });
    } else if (isOpen && !isEditMode) {
      setFormData({
        name: '',
        description: '',
        viewableStatuses: [],
        changeableStatuses: [],
        enabled: true,
      });

      setErrors({
        name: '',
        viewableStatuses: '',
        changeableStatuses: '',
      });
    }
  }, [isOpen, isEditMode, initialData, workflowStatuses]);

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      viewableStatuses: [],
      changeableStatuses: [],
      enabled: true,
    });

    setErrors({
      name: '',
      viewableStatuses: '',
      changeableStatuses: '',
    });

    onClose();
  };

  const ErrorWithIcon = ({ message }) => {
    if (!message) return null;

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <img src={VectorIcon} alt="error" width={14} height={14} />
        <span>{message}</span>
      </span>
    );
  };

  const handleSubmit = async () => {
    const schema = Yup.object({
      name: Yup.string().trim().required('Name is required').max(50),

      viewableStatuses: Yup.array()
        .min(1, 'At least one status is required')
        .required('At least one status is required'),

      changeableStatuses: Yup.array()
        .min(1, 'At least one status is required')
        .required('At least one status is required'),
    });

    try {
      await schema.validate(formData, { abortEarly: false });

      const dataToSend = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        viewableStatusIds: formData.viewableStatuses.map((s) => s.value),
        changeableStatusIds: formData.changeableStatuses.map((s) => s.value),
        enabled: formData.enabled,
      };

      if (isEditMode) {
        onUpdate(initialData.id, dataToSend);
      } else {
        onCreate(dataToSend);
      }

      handleClose();
    } catch (err) {
      if (err.inner) {
        const mappedErrors = {};

        err.inner.forEach((e) => {
          mappedErrors[e.path] = e.message;
        });

        setErrors(mappedErrors);
      }
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;

    setFormData({ ...formData, [field]: value });

    if (field === 'name') {
      let error = '';

      if (!value.trim()) {
        error = 'Name is required';
      }

      setErrors((prev) => ({ ...prev, name: error }));
    }
  };

  const handleViewableStatusChange = (selectedOptions) => {
    const value = selectedOptions || [];

    setFormData({ ...formData, viewableStatuses: value });

    setErrors((prev) => ({
      ...prev,
      viewableStatuses: value.length ? '' : 'At least one status is required',
    }));
  };

  const handleChangeableStatusChange = (selectedOptions) => {
    const value = selectedOptions || [];

    setFormData({ ...formData, changeableStatuses: value });

    setErrors((prev) => ({
      ...prev,
      changeableStatuses: value.length ? '' : 'At least one status is required',
    }));
  };

  const handleSwitchToggle = () => {
    setFormData({ ...formData, enabled: !formData.enabled });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit User Privilege' : 'Create User Privilege'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <ModalFormContent>
        <div style={{ position: 'relative' }}>
          <Input
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="Enter privilege name"
            required
            maxLength={50}
            error={!!errors.name}
          />
          {errors.name && (
            <ErrorText>
              <ErrorWithIcon message={errors.name} />
            </ErrorText>
          )}
        </div>

        <TextArea
          label="Description (Optional)"
          value={formData.description}
          onChange={handleInputChange('description')}
          placeholder="Enter description"
          rows={3}
          maxLength={250}
        />

        <div style={{ position: 'relative' }}>
          <Select
            label="Viewable Statuses"
            value={formData.viewableStatuses}
            onChange={handleViewableStatusChange}
            options={statusOptions}
            placeholder="Select viewable statuses"
            isMulti
            error={!!errors.viewableStatuses}
          />
          {errors.viewableStatuses && (
            <ErrorText>
              <ErrorWithIcon message={errors.viewableStatuses} />
            </ErrorText>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <Select
            label="Changeable Statuses"
            value={formData.changeableStatuses}
            onChange={handleChangeableStatusChange}
            options={statusOptions}
            placeholder="Select changeable statuses"
            isMulti
            error={!!errors.changeableStatuses}
          />
          {errors.changeableStatuses && (
            <ErrorText>
              <ErrorWithIcon message={errors.changeableStatuses} />
            </ErrorText>
          )}
        </div>

        <SwitchWrapper>
          <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} />
          {formData.enabled ? 'Enabled' : 'Disabled'}
        </SwitchWrapper>
      </ModalFormContent>
    </Modal>
  );
};

export default UserPrivilegeModal;
